/**
 * P2 movement breadcrumbs — pure trail-builder.
 *
 * Given a parsed battle and the current tick, produce up to N most
 * recent *distinct* tile positions per mech ≤ current tick, excluding
 * the position the mech occupies right now (the chassis itself sits
 * there and the breadcrumb would Z-fight).
 *
 * Pure function of `(battle, currentTick)` — scrubbing the timeline
 * backward rewinds the trail cleanly. No React / Three deps so this
 * unit-tests as plain TS.
 */

import type { Hex, ParsedBattle } from "@pilot-protocol/replay-parser";

export interface Breadcrumb {
  /** Hex tile the mech occupied. */
  pos: Hex;
  /** 1 = most recent past, N = oldest. Drives opacity ramp. */
  rank: number;
}

export const BREADCRUMB_TRAIL_LENGTH = 4;

function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

export function buildTrail(
  battle: ParsedBattle,
  currentTick: number,
  side: "A" | "B",
  trailLength: number = BREADCRUMB_TRAIL_LENGTH,
): Breadcrumb[] {
  const max = Math.min(currentTick, battle.states.length - 1);
  if (max < 0) return [];
  const distinct: Hex[] = [];
  let lastKey: string | null = null;
  for (let i = max; i >= 0; i--) {
    const mech = side === "A" ? battle.states[i].mech_a : battle.states[i].mech_b;
    const k = hexKey(mech.position);
    if (k !== lastKey) {
      distinct.push(mech.position);
      lastKey = k;
      // Stop once we've gathered enough: 1 current + `trailLength` past.
      if (distinct.length > trailLength) break;
    }
  }
  // distinct[0] is the current-tick position — chassis is there; skip it.
  return distinct.slice(1).map((pos, i) => ({ pos, rank: i + 1 }));
}
